import React, { useState, useMemo, lazy, Suspense } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FieldCard from '../../components/FieldCard';
import { fields, districts, generateTimeSlots, getDisplayPrice } from '../../data/mockData';
import { isSlotBlockedBySubscription } from '../../data/subscriptionStore';
import { Field } from '../../types';

const MapView = lazy(() => import('../../components/MapView'));

function haversineKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const toRad = (value: number) => (value * Math.PI) / 180;
  const earthRadius = 6371;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const first =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  const second = 2 * Math.atan2(Math.sqrt(first), Math.sqrt(1 - first));
  return earthRadius * second;
}

const FieldsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialDate = searchParams.get('date') || (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  const [selectedDistrict, setSelectedDistrict] = useState(searchParams.get('district') || '');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [selectedTime, setSelectedTime] = useState(searchParams.get('time') || '');
  const [hotWaterOnly, setHotWaterOnly] = useState(false);
  const [cafeteriaOnly, setCafeteriaOnly] = useState(false);
  const [cameraOnly, setCameraOnly] = useState(false);
  const [shoeRentalOnly, setShoeRentalOnly] = useState(false);
  const [fieldTypeFilter, setFieldTypeFilter] = useState<'all' | 'open' | 'closed'>('all');
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(600);
  const [radiusKm, setRadiusKm] = useState(searchParams.get('nearby') === '1' ? 12 : 0);
  const [locationLoading, setLocationLoading] = useState(false);
  const [sortBy, setSortBy] = useState('rating');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'map'>('grid');
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
  const rawLat = searchParams.get('lat');
  const rawLng = searchParams.get('lng');
  const initialLat = rawLat !== null ? Number(rawLat) : NaN;
  const initialLng = rawLng !== null ? Number(rawLng) : NaN;
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(
    Number.isFinite(initialLat) && Number.isFinite(initialLng) ? { lat: initialLat, lng: initialLng } : null
  );
  const hasUserLocation = Boolean(userLocation);
  const normalizedMinPrice = Math.min(minPrice, maxPrice);
  const normalizedMaxPrice = Math.max(minPrice, maxPrice);

  const filteredFields = useMemo(() => {
    let result = [...fields];

    if (selectedDistrict) {
      result = result.filter(f => f.district === selectedDistrict);
    }
    if (selectedSize) {
      result = result.filter(f => (f.sizeOptions || [f.size]).includes(selectedSize as any));
    }
    if (hotWaterOnly) {
      result = result.filter((f) => f.amenities.hotWater);
    }
    if (cafeteriaOnly) {
      result = result.filter((f) => f.amenities.cafeteria);
    }
    if (cameraOnly) {
      result = result.filter((f) => f.amenities.cameraSecurity);
    }
    if (shoeRentalOnly) {
      result = result.filter((f) => f.amenities.shoeRental);
    }
    if (fieldTypeFilter !== 'all') {
      result = result.filter((f) => {
        if (fieldTypeFilter === 'open') {
          return f.fieldType === 'outdoor' || f.roofType === 'none' || f.roofType === 'open';
        }
        return f.fieldType === 'indoor' || f.roofType === 'closed' || f.roofType === 'retractable';
      });
    }
    if (selectedTime) {
      const [h, m] = selectedTime.split(':').map((item) => Number(item));
      const selectedHourValue = Number(((h || 0) + ((m || 0) / 60)).toFixed(2));
      result = result.filter((f) => {
        if (f.isReservationPaused) return false;
        const slots = generateTimeSlots(f.id, selectedDate);
        const slot = slots.find((item) => Math.abs(item.hour - selectedHourValue) < 0.001);
        if (!slot || slot.isBooked) return false;
        const label = slot.label || selectedTime;
        return !isSlotBlockedBySubscription(f.id, selectedDate, label, undefined);
      });
    }
    result = result.filter((f) => {
      const displayPrice = getDisplayPrice(f);
      return displayPrice >= normalizedMinPrice && displayPrice <= normalizedMaxPrice;
    });
    if (radiusKm > 0 && userLocation) {
      result = result.filter((f) => haversineKm(userLocation.lat, userLocation.lng, f.lat, f.lng) <= radiusKm);
    }

    switch (sortBy) {
      case 'price-asc':
        result.sort((a, b) => getDisplayPrice(a) - getDisplayPrice(b));
        break;
      case 'price-desc':
        result.sort((a, b) => getDisplayPrice(b) - getDisplayPrice(a));
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name, 'tr'));
        break;
    }

    return result;
  }, [
    selectedDistrict,
    selectedSize,
    hotWaterOnly,
    cafeteriaOnly,
    cameraOnly,
    shoeRentalOnly,
    fieldTypeFilter,
    selectedTime,
    selectedDate,
    normalizedMinPrice,
    normalizedMaxPrice,
    radiusKm,
    userLocation,
    sortBy,
  ]);

  const renderedFields: Array<{ field: Field; distanceKm?: number }> = filteredFields
    .map((field) => ({
      field,
      distanceKm: userLocation ? haversineKm(userLocation.lat, userLocation.lng, field.lat, field.lng) : undefined,
    }))
    .sort((a, b) => {
      if (typeof a.distanceKm !== 'number' || typeof b.distanceKm !== 'number') return 0;
      return a.distanceKm - b.distanceKm;
    });

  const clearFilters = () => {
    setSelectedDistrict('');
    setSelectedSize('');
    setSelectedTime('');
    setHotWaterOnly(false);
    setCafeteriaOnly(false);
    setCameraOnly(false);
    setShoeRentalOnly(false);
    setFieldTypeFilter('all');
    setMinPrice(0);
    setMaxPrice(600);
    setRadiusKm(0);
    setSortBy('rating');
  };

  const handleMapFieldClick = (fieldId: string) => {
    navigate(`/saha/${fieldId}`);
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) return;
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        if (radiusKm === 0) setRadiusKm(10);
        setLocationLoading(false);
      },
      () => {
        setLocationLoading(false);
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Halı Sahalar</h1>
            <p className="text-gray-500 mt-1">
              {hasUserLocation && radiusKm > 0
                ? `Konumunuza ${radiusKm} km içindeki sahalar listeleniyor`
                : `${renderedFields.length} saha bulundu`}
            </p>
          </div>
          <div className="flex items-center space-x-3 mt-4 md:mt-0">
            {/* View Toggle */}
            <div className="flex bg-white border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 text-sm font-medium transition-colors ${
                  viewMode === 'grid' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
                title="Liste Görünümü"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`px-3 py-2 text-sm font-medium transition-colors ${
                  viewMode === 'map' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
                title="Harita Görünümü"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
              </button>
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden flex items-center space-x-2 bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-700"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <span>Filtreler</span>
            </button>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:outline-none focus:border-primary-500"
            >
              <option value="rating">Puana Göre</option>
              <option value="price-asc">Fiyat (Düşükten Yükseğe)</option>
              <option value="price-desc">Fiyat (Yüksekten Düşüğe)</option>
              <option value="name">İsme Göre</option>
            </select>
          </div>
        </div>

        {/* Map View */}
        {viewMode === 'map' && (
          <div className="mb-8">
            <Suspense fallback={
              <div className="h-[500px] bg-gray-100 rounded-xl flex items-center justify-center border border-gray-200">
                <div className="text-center">
                  <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">Harita yükleniyor...</p>
                </div>
              </div>
            }>
              <MapView
                fields={renderedFields.map(r => r.field)}
                height="500px"
                onFieldClick={handleMapFieldClick}
                onFieldHover={(fieldId) => setActiveFieldId(fieldId)}
                selectedFieldId={activeFieldId || undefined}
                showUserLocation={hasUserLocation}
                userLocation={userLocation}
              />
            </Suspense>
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-8">
          {/* Filters Sidebar */}
          <div className={`md:w-72 flex-shrink-0 ${showFilters ? 'block' : 'hidden md:block'}`}>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-gray-900 font-semibold text-lg">Filtreler</h2>
                <button
                  onClick={clearFilters}
                  className="text-primary-600 text-sm hover:text-primary-500"
                >
                  Temizle
                </button>
              </div>

              {/* District Filter */}
              <div className="mb-6">
                <label className="block text-gray-500 text-sm mb-2">İlçe</label>
                <select
                  value={selectedDistrict}
                  onChange={(e) => setSelectedDistrict(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 text-sm focus:outline-none focus:border-primary-500"
                >
                  <option value="">Tüm İlçeler</option>
                  {districts.map((d) => (
                    <option key={d} value={d}>İstanbul / {d}</option>
                  ))}
                </select>
              </div>

              {/* Date / Time Filter */}
              <div className="mb-6 space-y-3">
                <div>
                  <label className="block text-gray-500 text-sm mb-2">Tarih</label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-gray-500 text-sm mb-2">Saat Filtresi</label>
                  <select
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-sm"
                  >
                    <option value="">Saat fark etmez</option>
                    {Array.from({ length: 18 }, (_, idx) => {
                      const hour = 6 + idx;
                      const value = `${String(hour).padStart(2, '0')}:00`;
                      return <option key={value} value={value}>{value}</option>;
                    })}
                  </select>
                </div>
              </div>

              {/* Facility Filters */}
              <div className="mb-6">
                <label className="block text-gray-500 text-sm mb-2">Tesis Özellikleri</label>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setHotWaterOnly((v) => !v)} className={`px-3 py-2 rounded-lg text-xs border ${hotWaterOnly ? 'bg-primary-600 text-white border-primary-600' : 'bg-white border-gray-300 text-gray-700'}`}>Sıcak Su</button>
                  <button onClick={() => setCafeteriaOnly((v) => !v)} className={`px-3 py-2 rounded-lg text-xs border ${cafeteriaOnly ? 'bg-primary-600 text-white border-primary-600' : 'bg-white border-gray-300 text-gray-700'}`}>Kafeterya</button>
                  <button onClick={() => setCameraOnly((v) => !v)} className={`px-3 py-2 rounded-lg text-xs border ${cameraOnly ? 'bg-primary-600 text-white border-primary-600' : 'bg-white border-gray-300 text-gray-700'}`}>Kamera</button>
                  <button onClick={() => setShoeRentalOnly((v) => !v)} className={`px-3 py-2 rounded-lg text-xs border ${shoeRentalOnly ? 'bg-primary-600 text-white border-primary-600' : 'bg-white border-gray-300 text-gray-700'}`}>Krampon Kiralama</button>
                </div>
              </div>

              {/* Open/Closed Filter */}
              <div className="mb-6">
                <label className="block text-gray-500 text-sm mb-2">Saha Türü</label>
                <div className="flex gap-2">
                  <button onClick={() => setFieldTypeFilter('all')} className={`px-3 py-2 rounded-lg text-xs border ${fieldTypeFilter === 'all' ? 'bg-primary-600 text-white border-primary-600' : 'bg-white border-gray-300 text-gray-700'}`}>Tümü</button>
                  <button onClick={() => setFieldTypeFilter('open')} className={`px-3 py-2 rounded-lg text-xs border ${fieldTypeFilter === 'open' ? 'bg-primary-600 text-white border-primary-600' : 'bg-white border-gray-300 text-gray-700'}`}>Açık</button>
                  <button onClick={() => setFieldTypeFilter('closed')} className={`px-3 py-2 rounded-lg text-xs border ${fieldTypeFilter === 'closed' ? 'bg-primary-600 text-white border-primary-600' : 'bg-white border-gray-300 text-gray-700'}`}>Kapalı</button>
                </div>
              </div>

              {/* Size Filter */}
              <div className="mb-6">
                <label className="block text-gray-500 text-sm mb-2">Saha Boyutu</label>
                <div className="flex flex-wrap gap-2">
                  {['', '5v5', '6v6', '7v7', '8v8', '9v9', '10v10', '11v11'].map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedSize === size
                          ? 'bg-primary-600 text-white'
                          : 'bg-white text-gray-700 hover:text-gray-900 border border-gray-300'
                      }`}
                    >
                      {size || 'Tümü'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div className="mb-6">
                <label className="block text-gray-500 text-sm mb-2">
                  Fiyat Aralığı: {normalizedMinPrice}₺ - {normalizedMaxPrice}₺
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500">Min</label>
                    <input
                      type="number"
                      min={0}
                      max={10000}
                      step={10}
                      value={minPrice}
                      onChange={(e) => setMinPrice(Math.max(0, Number(e.target.value) || 0))}
                      className="mt-1 w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Max</label>
                    <input
                      type="number"
                      min={0}
                      max={10000}
                      step={10}
                      value={maxPrice}
                      onChange={(e) => setMaxPrice(Math.max(0, Number(e.target.value) || 0))}
                      className="mt-1 w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900"
                      placeholder="600"
                    />
                  </div>
                </div>
              </div>

              {/* Radius Filter */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-gray-500 text-sm">KM Filtresi</label>
                  <button
                    type="button"
                    onClick={handleUseMyLocation}
                    disabled={locationLoading}
                    className="text-xs text-primary-700 border border-primary-200 bg-primary-50 rounded px-2 py-1 disabled:opacity-50"
                  >
                    {locationLoading ? 'Konum Alınıyor' : 'Konumumu Kullan'}
                  </button>
                </div>
                <label className="block text-xs text-gray-500 mb-2">
                  {radiusKm === 0 ? 'Sınır yok' : `${radiusKm} km içinde`}
                </label>
                <input
                  type="range"
                  min={0}
                  max={30}
                  step={1}
                  value={radiusKm}
                  onChange={(e) => setRadiusKm(Number(e.target.value))}
                  className="w-full accent-primary-500"
                />
              </div>

              {/* Quick Stats */}
              <div className="pt-4 border-t border-gray-200">
                <p className="text-gray-500 text-xs">
                  {renderedFields.length} saha gösteriliyor
                </p>
              </div>
            </div>
          </div>

          {/* Field Grid */}
          <div className="flex-1">
            {renderedFields.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {renderedFields.map(({ field, distanceKm }) => (
                  <div
                    key={field.id}
                    id={`field-card-${field.id}`}
                    onMouseEnter={() => setActiveFieldId(field.id)}
                    onMouseLeave={() => setActiveFieldId((current) => (current === field.id ? null : current))}
                  >
                    <FieldCard
                      field={field}
                      distanceKm={distanceKm}
                      isHighlighted={activeFieldId === field.id}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-gray-900 text-lg font-semibold mb-2">Saha Bulunamadı</h3>
                <p className="text-gray-500">Filtreleri değiştirerek tekrar deneyin.</p>
                <button
                  onClick={clearFilters}
                  className="mt-4 text-primary-600 hover:text-primary-500 font-medium"
                >
                  Filtreleri Temizle
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FieldsPage;
